import React, { useEffect, useLayoutEffect } from 'react'
import CircularProgress from '@material-ui/core/CircularProgress'
import Tooltip from '@material-ui/core/Tooltip'
import CloseIcon from '@material-ui/icons/Close'
import CheckIcon from '@material-ui/icons/Check'
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline'
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import { AdCreative, AssetApprovalStatus, FBAdPreviewFormat } from '@marketing-milk/interfaces'
import { useFetchCreativePreview } from 'hooks/creatives/fetch-creative-preview'
import { getIFrameSource } from 'components/ad-preview'
import { MobilePreview } from '../../creatives/creative-wizard/ad-preview/views/mobile-preview/MobilePreview'
import { radius, status, SurfacePalette, type as typo } from '../tokens'
import { PacketModalShell, modalGhostButtonStyle } from './PacketModalShell'

interface Props {
  creative: AdCreative
  palette: SurfacePalette
  onClose: () => void
  /** Edit-mode only: open the creative's native editor for source edits. */
  onOpenSource?: (creative: AdCreative) => void
  /** Record an AE-attested client approval. Shown when the creative is approvable. */
  onApprove?: (creative: AdCreative) => void
  /** Clear an existing approval. Shown when the creative is already Approved. */
  onRemoveApproval?: (creative: AdCreative) => void
}

const PREVIEW_WIDTH = 380

/** Approval can be recorded from any pre-published state that isn't already Approved. */
const isApprovable = (status: AssetApprovalStatus) =>
  status !== AssetApprovalStatus.Approved && status !== AssetApprovalStatus.Published

/** A 36px icon button for the preview's side rail; tooltip carries the label. */
const RailIcon = ({
  palette,
  title,
  onClick,
  accent,
  danger,
  disabled,
  commentTrigger,
  children,
}: {
  palette: SurfacePalette
  title: string
  onClick?: () => void
  /** Primary action (filled accent) — Approve. */
  accent?: boolean
  /** Destructive action (red-tinted outline, not filled) — Remove approval. */
  danger?: boolean
  /** Greys out and blocks the action (e.g. Approve while a fresh preview loads). */
  disabled?: boolean
  /** Marks this as the shared comment trigger picked up by `useCommentAnchors`. */
  commentTrigger?: boolean
  children: React.ReactNode
}) => (
  <Tooltip title={title} placement="right">
    <span>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={title}
        data-comment-trigger={commentTrigger ? 'true' : undefined}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          padding: 0,
          border: `1px solid ${
            accent ? palette.accent : danger ? status.danger : palette.hairline
          }`,
          background: accent ? palette.accent : 'transparent',
          color: accent ? '#fff' : danger ? status.danger : palette.textMuted,
          borderRadius: radius.sm,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.4 : 1,
          lineHeight: 0,
        }}
      >
        {children}
      </button>
    </span>
  </Tooltip>
)

/**
 * Packet-styled dialog that renders the live Facebook mobile-feed Ad Preview for
 * a single sprint creative, reusing the Creative Wizard preview pipeline
 * (`useFetchCreativePreview` -> `MobilePreview`). The preview is the only surface
 * that shows the full ad (copy + headline + CTA, not just the image), so the
 * meeting-time actions live on a slim icon rail beside it — Comment (on the
 * creative object), Approve / Remove approval, Open in Creative Wizard, and Close
 * — each labelled via tooltip. A thin icon rail keeps the chrome out of the
 * vertical budget (so the whole ad fits without scrolling) and barely offsets the
 * ad from center. Mounted on demand from the Creative section in both Edit and
 * Present; the hosting `useCreativePreview` hook locks Present deck navigation
 * while it is open.
 *
 * The rail is wrapped in the `creative` comment anchor so the shared
 * `useCommentAnchors` delegation opens the same thread as the card — the preview
 * modal is a DOM descendant of the comment root, so the click bubbles normally.
 */
export function CreativePreviewModal({
  creative,
  palette,
  onClose,
  onOpenSource,
  onApprove,
  onRemoveApproval,
}: Props) {
  const { refreshPreview, adCreativePreview, loadingPreview, refreshing, freshPreviewReady } =
    useFetchCreativePreview(creative.id, FBAdPreviewFormat.MOBILE_FEED_STANDARD)

  // Force a live refresh on mount: a hover-prefetched preview shows instantly, but
  // the creative may have been edited since, so we always refetch fresh (keeping
  // the cached HTML on screen until it arrives) before the AE can approve it.
  // useLayoutEffect so `refreshing` flips true before the first paint, leaving no
  // frame where the cached preview is shown with Approve still enabled.
  useLayoutEffect(() => {
    refreshPreview()
  }, [refreshPreview])

  // When the packet tab regains focus (the AE returns from editing in the wizard
  // tab), refetch so the preview reflects the saved edit.
  useEffect(() => {
    const onFocus = () => refreshPreview()
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshPreview()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [refreshPreview])

  const iframeSource = adCreativePreview ? getIFrameSource(adCreativePreview) : ''
  const approved = creative.reviewStatus === AssetApprovalStatus.Approved
  const approveDisabled = loadingPreview || refreshing || !freshPreviewReady

  const rail = (
    <div
      data-comment-anchor-type="creative"
      data-comment-anchor-id={String(creative.id)}
      data-comment-label={creative.name}
      style={{ width: 36, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <RailIcon palette={palette} title="Close" onClick={onClose}>
        <CloseIcon style={{ fontSize: 18 }} />
      </RailIcon>
      <RailIcon palette={palette} title="Comment" commentTrigger>
        <ChatBubbleOutlineIcon style={{ fontSize: 16 }} />
      </RailIcon>
      {onApprove && isApprovable(creative.reviewStatus) && (
        <RailIcon
          palette={palette}
          // Block approval until the forced refresh resolves so the AE can't
          // approve hover-cached, possibly pre-edit HTML still on screen.
          title={approveDisabled ? 'Loading latest preview…' : 'Approve'}
          accent
          disabled={approveDisabled}
          onClick={() => onApprove(creative)}
        >
          <CheckIcon style={{ fontSize: 18 }} />
        </RailIcon>
      )}
      {onRemoveApproval && approved && (
        <RailIcon
          palette={palette}
          title="Remove approval"
          danger
          onClick={() => onRemoveApproval(creative)}
        >
          <RemoveCircleOutlineIcon style={{ fontSize: 16 }} />
        </RailIcon>
      )}
      {onOpenSource && (
        <RailIcon
          palette={palette}
          title="Open in Creative Wizard"
          onClick={() => onOpenSource(creative)}
        >
          <OpenInNewIcon style={{ fontSize: 16 }} />
        </RailIcon>
      )}
    </div>
  )

  return (
    <PacketModalShell
      palette={palette}
      hideHeader
      padding={12}
      onClose={onClose}
      maxWidth={464}
      closeOnBackdropClick
    >
      <div style={{ display: 'flex', gap: 12 }}>
        {rail}
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            minHeight: 360,
            minWidth: 0,
          }}
        >
          {loadingPreview ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                color: palette.textMuted,
                marginTop: 120,
              }}
            >
              <CircularProgress size={22} style={{ color: palette.accent }} />
              <span style={{ fontSize: typo.size.body }}>Generating preview…</span>
            </div>
          ) : iframeSource ? (
            // Render the FB mobile preview directly at ~device width (so the ad
            // doesn't overflow horizontally), frameless (near-zero bezel), and as
            // tall as the viewport allows — falling back to internal scroll only
            // for ads taller than the screen.
            <MobilePreview
              format={FBAdPreviewFormat.MOBILE_FEED_STANDARD}
              iframeSource={iframeSource}
              width={PREVIEW_WIDTH}
              height="min(900px, 92vh)"
              frameless
            />
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 14,
                textAlign: 'center',
                color: palette.textMuted,
                marginTop: 120,
              }}
            >
              <span style={{ fontSize: typo.size.body }}>
                Couldn’t generate a preview from Facebook.
              </span>
              <button
                type="button"
                onClick={() => refreshPreview()}
                style={modalGhostButtonStyle(palette)}
              >
                Retry
              </button>
            </div>
          )}
        </div>
      </div>
    </PacketModalShell>
  )
}
